<script setup lang="ts">
import { Calendar, Search } from 'lucide-vue-next'
import { ComboboxInput, type ComboboxInputProps, useForwardProps } from 'radix-vue'
import { computed, type HTMLAttributes, ref } from 'vue'
import { Popover, PopoverContent, PopoverTrigger } from '@/foundation/popover'
import { RangeCalendar } from '@/foundation/range-calendar'
import { cn } from '@/foundation/utils'

defineOptions({
  inheritAttrs: false,
})

const props = defineProps<
  ComboboxInputProps & {
    class?: HTMLAttributes['class']
    showCalendar?: boolean
  }
>()

const emit = defineEmits<{
  'update:dateRange': [any]
}>()

const selectedDateRange = ref<any>()

const onDateRangeSelect = (range: any) => {
  if (!range?.start || !range?.end) return

  const startDate = new Date(range.start.year, range.start.month - 1, range.start.day)
  const endDate = new Date(range.end.year, range.end.month - 1, range.end.day)

  selectedDateRange.value = {
    from: startDate,
    to: endDate,
  }
  emit('update:dateRange', selectedDateRange.value)
}

const delegatedProps = computed(() => {
  const { class: _, ...delegated } = props

  return delegated
})

const forwardedProps = useForwardProps(delegatedProps)
</script>

<template>
  <div class="flex items-center border-b px-3" cmdk-input-wrapper>
    <Search v-bind="{ ...$attrs }" class="mr-2 h-4 w-4 shrink-0 opacity-50 cursor-pointer" />
    <ComboboxInput
      v-bind="{ ...forwardedProps, ...$attrs }"
      :class="
        cn(
          'flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50',
          props.class,
        )
      "
    />
    <Popover>
      <PopoverTrigger>
        <Calendar v-if="showCalendar" class="h-4 w-4 shrink-0 opacity-50 cursor-pointer" />
      </PopoverTrigger>
      <PopoverContent class="w-auto p-0" align="end">
        <RangeCalendar
          v-model="selectedDateRange"
          initial-focus
          @update:start-value="onDateRangeSelect"
          @update:end-value="onDateRangeSelect"
        />
      </PopoverContent>
    </Popover>
  </div>
</template>
